class CartDiscountBase extends HTMLElement {
  constructor(dataPrefix) {
    super();
    this.dataPrefix = dataPrefix;
    this.productId = parseInt(this.dataset[`${dataPrefix}Product`]);
    this.productType = this.dataset[`${dataPrefix}ProductType`];
    this.threshold = parseInt(this.dataset[`${dataPrefix}Threshold`]);
    this.useType = this.dataset[`${dataPrefix}UseType`] === 'true';
  }

  connectedCallback() {
    setTimeout(() => this.fetchAndUpdate());

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cart-items') {
        const items = event?.cartData?.items ?? [];
        const qty = this.getProductQty(items);
        this.updateDisplay(qty);
      } else {
        this.fetchAndUpdate();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  fetchAndUpdate() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        const qty = this.getProductQty(cart.items);
        this.updateDisplay(qty);
      })
      .catch((e) => console.error(`[${this.tagName}] failed to fetch cart`, e));
  }

  getProductQty(items) {
    return items
      .filter((item) =>
        this.useType
          ? item.product_type === this.productType
          : item.product_id === this.productId
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  updateDisplay(qty) {
    const needed = this.threshold - qty;
    const wrapper = this.closest('.cart-discounts-wrap');

    if (qty === 0) {
      this.hidden = true;
      if (wrapper) wrapper.classList.remove('cart-discounts-wrap--active');
      return;
    }

    this.hidden = false;
    if (wrapper) wrapper.classList.add('cart-discounts-wrap--active');

    this.querySelector('.nudge-1').hidden = !(needed === 1);
    this.querySelector('.nudge-2').hidden = !(needed > 1);
    this.querySelector('.success').hidden = !(needed <= 0);
  }
}

class CartDiscountQuantity extends CartDiscountBase {
  constructor() {
    super('cdq');
  }
}

class CartBuyXGetY extends CartDiscountBase {
  constructor() {
    super('cbuyx');
  }
}

customElements.define('cart-discount-quantity', CartDiscountQuantity);
customElements.define('cart-buy-x-get-y', CartBuyXGetY);


class CartDiscountBundle extends HTMLElement {
  connectedCallback() {
    setTimeout(() => this.fetchAndUpdate());

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cart-items') {
        this.updateDisplay(event?.cartData?.items ?? []);
      } else {
        this.fetchAndUpdate();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  fetchAndUpdate() {
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => this.updateDisplay(cart.items))
      .catch((e) => console.error('[CartDiscountBundle] failed to fetch cart', e));
  }

  getInnerDiv() {
    return this.querySelector('.cart-discount');
  }

  getTrigger() {
    const div = this.getInnerDiv();
    if (!div) return null;

    const ignoreTrigger = div.dataset.ignore_trigger === 'true';
    if (ignoreTrigger) return null;

    const id = parseInt(div.dataset.cdbProduct_trigger_id);
    const type = div.dataset.cdbProduct_trigger_type;
    const useType = div.dataset.cdbUse_type === 'true';  // updated attribute name

    if (!id && !type) return null;

    return { id, type, useType };
  }

  getBundleItems() {
    const div = this.getInnerDiv();
    if (!div) return [];

    const useType = div.dataset.bundle_use_type === 'true';
    const items = [];
    let index = 1;

    while (div.dataset[`product_bundle_${index}_id`]) {
      items.push({
        id: parseInt(div.dataset[`product_bundle_${index}_id`]),
        type: div.dataset[`product_bundle_${index}_type`],
        useType,
      });
      index++;
    }

    return items;
  }

  isItemInCart(cartItems, bundleItem) {
    return cartItems.some((cartItem) =>
      bundleItem.useType
        ? cartItem.product_type === bundleItem.type
        : cartItem.product_id === bundleItem.id
    );
  }

  updateDisplay(cartItems) {
    const div = this.getInnerDiv();
    const trigger = this.getTrigger();
    const bundleItems = this.getBundleItems();
    const bundleUseType = div.dataset.bundle_use_type === 'true';

    if (trigger) {
      const triggerInCart = cartItems.some((cartItem) =>
        trigger.useType
          ? cartItem.product_type === trigger.type
          : cartItem.product_id === trigger.id
      );

      if (!triggerInCart) {
        this.hidden = true;
        this.closest('.cart-discounts-wrap')?.classList.remove('cart-discounts-wrap--active');
        return;
      }
    } else {
      const noneInCart = bundleItems.every((item) => !this.isItemInCart(cartItems, item));

      if (noneInCart) {
        this.hidden = true;
        this.closest('.cart-discounts-wrap')?.classList.remove('cart-discounts-wrap--active');
        return;
      }
    }

    const allInCart = bundleItems.every((item) => this.isItemInCart(cartItems, item));

    // Update item-added class on each link based on its own product ID/type
    this.querySelectorAll('.cart-bundle-link').forEach((link) => {
      const linkProductId = parseInt(link.dataset.productLinkId);
      const linkProductType = link.dataset.productLinkType;
      const isTrigger = link.classList.contains('cart-bundle-link--trigger');
      const useTypeForLink = isTrigger ? trigger?.useType : bundleUseType;

      const inCart = cartItems.some((cartItem) =>
        useTypeForLink
          ? cartItem.product_type === linkProductType
          : cartItem.product_id === linkProductId
      );
      link.classList.toggle('item-added', inCart);
    });

    this.hidden = false;
    this.closest('.cart-discounts-wrap')?.classList.add('cart-discounts-wrap--active');

    this.querySelector('.nudge-1').hidden = allInCart;
    this.querySelector('.success').hidden = !allInCart;
  }
}

customElements.define('cart-discount-bundle', CartDiscountBundle);